export type ServiceFeature = {
  title: string;
  items: string[];
};

export type Item = {
  heroImage?: string;
  slug: string;            // e.g. "backup"
  title: string;           // e.g. "Backup"
  heading?: string;           // e.g. "Backup"
  heading1?: string;           // e.g. "Backup"
  description: string;     // short paragraph
  description1?: string;     // short paragraph
  image?: string;          // main illustration shown in details section
  featureSections?: ServiceFeature[]; // multiple titled lists
  highlights?: string[]; // hero highlight bullets or cards
  heroCards?: { title: string; icon?: string }[];
  offeringCards?: { title: string; icon?: string; items: string[] }[];
  complianceCards?: { title: string; icon?: string }[];
  industryCards?: { title: string; description: string; image?: string }[];
  whyChooseItems?: { title: string; icon?: string }[];
  certifications?: { label: string; icon?: string }[];
  sectionBlocks?: {
    title: string;
    description?: string;
    items: string[];
  }[];
  ctaText?: string;
  ctaSubText?: string;
  template?: 'default' | 'imageTop' | 'imageRight'; // layout template
  benefits?: string[];     // key benefits
  process?: string[];      // process steps
};

export const services: Item[] = [
  {
    slug: 'ai-services',
    title: 'AI Services',
    // Replace this file with any AI Services image you want to display on the card.
    image: '/images/Generative AI.png',
    description:
      'Enterprise AI licensing, implementation, custom development, and intelligent automation solutions tailored to modern business needs.',
  },
  {
    slug: 'compliance-cyber-security-services',
    title: 'Compliance & Cyber security Services',
    image: '/images/compliance&cybersecurity.png',
    description:
      'End-to-end compliance advisory and cybersecurity services covering risk assessment, governance, vulnerability management, identity protection, and incident readiness.',
  },
  {
     heroImage:"/images/ITCloud.png",
    slug: 'it-cloud',
    title: 'IT Cloud',
    template: 'default',
    heading: 'Transform Your Business with Cloud Solutions',
    description:
      'As a leading Cloud Managed Service Provider (MSP), HTPL specializes in comprehensive cloud transformation services. We help businesses migrate, deploy, and optimize their infrastructure across major cloud platforms including Microsoft Azure, AWS, and Google Cloud Platform. Our expertise in DevOps practices, automation, and cloud-native technologies enables organizations to achieve scalability, cost-efficiency, and enhanced security.',
    heading1: 'Why Choose HTPL for Cloud Services?',
    description1: 'Our cloud experts bring years of experience in enterprise-grade cloud implementations. We follow industry best practices for security, compliance, and performance optimization. From initial assessment to post-migration support, we ensure your cloud journey is smooth and successful.',
    image: '/images/ITCloud.gif',
    benefits: [
      'Reduced operational costs by up to 40%',
      '99.9% uptime guarantee with robust disaster recovery',
      'Enhanced security with enterprise-grade protection',
      'Scalable infrastructure that grows with your business',
      '24/7 monitoring and support'
    ],
    process: [
      'Cloud Readiness Assessment',
      'Migration Strategy Planning',
      'Infrastructure Setup & Configuration',
      'Data Migration & Testing',
      'Go-Live & Optimization',
      'Ongoing Management & Support'
    ],
    featureSections: [
      {
        title: 'Cloud Platforms & Services',
        items: [
          'Microsoft Azure - Complete ecosystem integration',
          'Amazon Web Services (AWS) - Scalable computing power',
          'Google Cloud Platform (GCP) - Advanced analytics & AI',
          'Multi-cloud and hybrid cloud architectures',
          'Cloud-native application development'
        ],
      },
      {
        title: 'Infrastructure Services',
        items: [
          'Disaster Recovery (DC-DR) solutions',
          'Automated data backup & restore',
          'ERP system hosting and management',
          'Database migration and optimization',
          'Network security and monitoring'
        ],
      },
    ],
  },
  {
    heroImage:"/images/Business.png",
    slug: 'business-apps',
    title: 'Business Apps',
    template: 'imageTop',
    heading: 'Optimize Your Business Operations',
    description:
      'HTPL provides comprehensive business application solutions designed to enhance productivity, streamline workflows, and improve collaboration across your organization. We specialize in Microsoft 365, Google Workspace, and custom business applications that integrate seamlessly with your existing infrastructure.',
    heading1: 'Complete Business Application Suite',
    description1: 'From email systems to advanced analytics, we provide end-to-end business application services. Our solutions include proper licensing optimization, security implementation, and ongoing support to ensure maximum ROI from your technology investments.',
    image: '/images/Business.gif',
    benefits: [
      'Improved team collaboration and productivity',
      'Reduced licensing costs through optimization',
      'Enhanced data security and compliance',
      'Seamless integration with existing systems',
      'Advanced analytics and reporting capabilities'
    ],
    process: [
      'Business Requirements Analysis',
      'Application Assessment & Planning',
      'Implementation & Configuration',
      'Data Migration & Integration',
      'User Training & Support',
      'Ongoing Optimization & Maintenance'
    ],
    featureSections: [
      {
        title: 'Productivity Suites',
        items: [
          'Microsoft 365 - Complete office suite with Teams integration',
          'Google Workspace - Cloud-based collaboration tools',
          'SharePoint development and customization',
          'OneDrive and Google Drive setup and migration',
          'Advanced email security and archiving'
        ],
      },
      {
        title: 'Analytics & Automation',
        items: [
          'Power BI - Advanced business intelligence dashboards',
          'Power Automate - Workflow automation solutions',
          'Power Apps - Custom business applications',
          'Microsoft Copilot Studio – AI-powered copilots and virtual assistants',
          'Generative AI and intelligent business automation solutions',
          'Data integration and ETL processes',
          'Custom reporting and analytics solutions'
        ],
      },
    ],
  },
  {
    heroImage:"/images/Backup.png",
    slug: 'backup',
    title: 'Acronis Backup',
    template: 'imageRight',
    heading: 'Acronis Cyber Protection and Backup Solutions',
    description:
      "Him Technology is a Platinum Partner of Acronis, delivering integrated backup, disaster recovery, endpoint security, anti-malware, and data protection through one unified platform.",
    heading1: 'Why Data Backup is Critical for Your Business',
    description1: 'Data loss can be catastrophic for any business. Our backup solutions provide multiple layers of protection with automated scheduling, real-time monitoring, and rapid recovery capabilities. We ensure your business continuity with reliable, tested backup systems that meet industry compliance standards.',
    image: '/images/Backup.gif',
    benefits: [
      'Zero data loss with continuous backup monitoring',
      'Rapid recovery with RTO/RPO optimization',
      'Cost-effective tiered storage solutions',
      'Automated backup scheduling and verification',
      'Compliance with industry data protection standards'
    ],
    process: [
      'Data Assessment & Risk Analysis',
      'Backup Strategy Development',
      'Infrastructure Setup & Configuration',
      'Initial Data Backup & Verification',
      'Automated Monitoring Implementation',
      'Regular Testing & Maintenance'
    ],
    featureSections: [
      {
        title: 'Storage Technologies',
        items: [
          'Tape backup systems - Long-term archival storage',
          'Disk-based backup - High-speed data recovery',
          'NAS (Network Attached Storage) - Centralized file storage',
          'SAN Solutions - High-performance block storage',
          'Hybrid cloud storage - Best of both worlds'
        ],
      },
      {
        title: 'Backup Strategies',
        items: [
          'Full backup - Complete system protection',
          'Incremental backup - Efficient daily backups',
          'Differential backup - Optimized recovery points',
          'Cloud backup - Offsite data protection',
          'Image backup - Complete system restoration',
          'Real-time replication - Zero data loss protection'
        ],
      },
    ],
  },
 
 
  {
    heroImage:"/images/ContentCloudCMS.png",
    slug: 'content-cloud-cms',
    heading:"What is the Content?",
    title: 'Content Cloud CMS',
    description:
      'Content is the interaction you have with your clients. Particularly, it’s the accumulation of texts, pictures, audios, and videos that customers consume to involve with your services. When you browse the latest products/article/tab on your favourite websites, that’s also content. News articles, banner ads, social media posts, and even something as fundamental as the logo on a mobile app are all different types of content. But, when we imply content in the context of customer engagement, it generally means some form of digital text, images, video, or audio.',
    image: '/images/ContentCloud.gif',
    heading1:"What is the Content Management System?",
    description1:"A CMS is a type of software that helps users create, alignment, and modify content on websites without the necessity for specialised technical knowledge. In simple words, a CMS is a tool that helps you create a website without requiring to write all the code from scratch or, sometimes, without even knowing how to code at all. Instead of building your own system for developing web pages, storing images, and other functions, the CMS manages all that basic infrastructure aspects for you to enable you to focus on other important parts of your website that involve interface with the user."
  }, 
  {
    heroImage:"/images/Security.png",
    slug: 'security',
    title: 'Security',
    template: 'default',
    heading: 'Advanced Cybersecurity Solutions',
    description:
      'HTPL provides comprehensive cybersecurity services to protect your organization from evolving cyber threats. Our multi-layered security approach includes advanced threat detection, data protection, network security, and compliance management. We implement industry-leading security technologies and best practices to safeguard your critical business assets.',
    heading1: 'Proactive Security Management',
    description1: 'Our security experts continuously monitor and analyze your IT infrastructure to identify and mitigate potential threats before they impact your business. We provide 24/7 security operations center (SOC) services, incident response, and regular security assessments to maintain the highest level of protection.',
    image: '/images/Security.gif',
    benefits: [
      'Reduced security incidents by up to 90%',
      'Compliance with industry regulations (GDPR, HIPAA, SOX)',
      'Real-time threat detection and response',
      'Comprehensive security awareness training',
      'Cost-effective security management'
    ],
    process: [
      'Security Assessment & Gap Analysis',
      'Security Strategy Development',
      'Implementation & Configuration',
      'Employee Training & Awareness',
      'Continuous Monitoring & Management',
      'Incident Response & Recovery'
    ],
    featureSections: [
      {
        title: 'Network & Infrastructure Security',
        items: [
          'Next-generation firewalls with advanced threat protection',
          'Intrusion detection and prevention systems (IDS/IPS)',
          'Virtual private networks (VPN) for secure remote access',
          'Network segmentation and access control',
          'SSL/TLS certificate management and encryption'
        ],
      },
      {
        title: 'Endpoint & Data Protection',
        items: [
          'Advanced antivirus and anti-malware solutions',
          'Endpoint detection and response (EDR)',
          'Data loss prevention (DLP) systems',
          'Mobile device management (MDM) solutions',
          'Hardware security modules (HSM) for key management',
          'Email security and phishing protection'
        ],
      },
    ],
  }, 
 
  {
    heroImage:"/images/ITInfrastructureHWandSW.png",
    slug: 'it-infrastructure-hw-and-sw',
    title: 'IT Infrastructure HW and SW',
    template: 'imageTop',
    heading: 'Complete IT Infrastructure Solutions',
    description:
      'HTPL provides end-to-end IT infrastructure solutions including hardware procurement, software licensing, and system integration services. We help organizations build robust, scalable, and cost-effective IT infrastructure that supports business growth and operational efficiency.',
    heading1: 'Hardware & Software Optimization',
    description1: 'Our infrastructure experts work closely with leading technology vendors to provide the best hardware and software solutions at competitive prices. We ensure optimal performance, reliability, and cost-effectiveness through careful planning and strategic procurement.',
    image: '/images/infrastructure.gif',
    benefits: [
      'Cost savings through volume licensing and partnerships',
      'Optimized performance with right-sized solutions',
      'Reduced downtime with reliable enterprise hardware',
      'Simplified management with integrated solutions',
      'Future-proof technology investments'
    ],
    process: [
      'Infrastructure Assessment & Planning',
      'Hardware & Software Selection',
      'Procurement & Vendor Management',
      'Installation & Configuration',
      'Testing & Quality Assurance',
      'Ongoing Support & Maintenance'
    ],
    featureSections: [
      {
        title: 'Server & Storage Solutions',
        items: [
          'Enterprise servers - Dell, HP, IBM, Lenovo',
          'High-performance storage systems (NAS/SAN)',
          'Virtualization platforms - VMware, Hyper-V',
          'Backup and disaster recovery appliances',
          'Cloud-ready infrastructure solutions'
        ],
      },
      {
        title: 'Network & Software Licensing',
        items: [
          'Enterprise switches and routers - Cisco, Juniper',
          'Load balancers and application delivery controllers',
          'Windows Server and SQL Server licensing',
          'Oracle database and middleware solutions',
          'Microsoft Office and productivity suites',
          'Security software and endpoint protection'
        ],
      },
    ],
  }, 
  {
    heroImage:"/images/TechnicalTrainingProgrammes.png",
    slug: 'technical-training-programmes',
    title: 'Technical Training Programmes',
    template: 'imageRight',
    heading: 'Advance Your Technical Skills',
    description:
      'HTPL provides comprehensive technical training programs designed to enhance skills in cutting-edge cloud technologies and emerging IT domains. Our training programs are tailored for technical professionals, corporate teams, and fresh graduates looking to build expertise in modern technology stacks.',
    heading1: 'Corporate Training Excellence',
    description1: 'With over 15 years of experience in technical training, HTPL offers industry-relevant courses based on thorough market research and client needs. Our training programs are constantly updated to include the latest technologies and best practices, ensuring participants gain practical, job-ready skills.',
    image: '/images/technical-training-programmes.gif',
    benefits: [
      'Industry-certified training programs',
      'Hands-on practical experience with real projects',
      'Expert trainers with industry experience',
      'Flexible training schedules and formats',
      'Job placement assistance for graduates'
    ],
    process: [
      'Training Needs Assessment',
      'Customized Curriculum Development',
      'Expert Trainer Assignment',
      'Interactive Training Delivery',
      'Practical Labs & Projects',
      'Certification & Job Support'
    ],
    featureSections: [
      {
        title: 'Training Categories',
        items: [
          'Cloud Technologies - AWS, Azure, GCP certification prep',
          'DevOps & Containerization - Docker, Kubernetes, CI/CD',
          'Data Analytics - Power BI, Tableau, Python, R',
          'Cybersecurity - Ethical hacking, security auditing',
          'Programming - Java, .NET, Python, JavaScript frameworks'
        ],
      },
      {
        title: 'Training Formats',
        items: [
          'Corporate training - Customized for organizational needs',
          'PSU training programs - Government sector specialization',
          'College graduate programs - Career-oriented skill development',
          'Online training - Flexible remote learning options',
          'Bootcamp programs - Intensive skill development',
          'Workshop series - Focused technology deep-dives'
        ],
      },
    ],
  }, 
  {
    heroImage:"/images/DevOpsandContainerization.png",
    slug: 'devops-and-containerization',
    title: 'DevOps and Containerization',
    template: 'imageTop',
    heading: 'CI/CD Pipeline as Code',
    description:
      'HTPL specializes in implementing robust DevOps practices and containerization solutions that streamline your software development lifecycle. We configure builds, tests, and deployments in code that is trackable and stored in centralized repositories. Our experts help teams deploy CI/CD pipelines through source code, enabling real-time monitoring and automated deployment processes.',
    heading1: 'Infrastructure as Code Excellence',
    description1: 'With advanced Infrastructure-as-Code (IaC) DevOps practices, we reduce human errors and improve consistency in infrastructure deployments. Our IaC framework automates the build and deployment of your cloud infrastructure, ensuring reproducible and scalable environments across development, testing, and production.',
    image: '/images/devops.gif',
    benefits: [
      'Faster deployment cycles with automated pipelines',
      'Reduced infrastructure costs through optimization',
      'Improved reliability with consistent environments',
      'Enhanced security with automated compliance checks',
      'Better collaboration between development and operations teams'
    ],
    process: [
      'Current State Assessment & Gap Analysis',
      'DevOps Strategy & Toolchain Selection',
      'CI/CD Pipeline Design & Implementation',
      'Infrastructure as Code Development',
      'Container Orchestration Setup',
      'Monitoring & Continuous Improvement'
    ],
    featureSections: [
      {
        title: 'Kubernetes & Container Management',
        items: [
          'Kubernetes cluster setup and management on Azure AKS, AWS EKS',
          'Docker containerization and image optimization',
          'Microservices architecture design and implementation',
          'Service mesh implementation with Istio/Linkerd',
          'Container security scanning and compliance',
          'Auto-scaling and resource optimization'
        ],
      },
      {
        title: 'CI/CD & Automation Tools',
        items: [
          'Azure DevOps, GitHub Actions, Jenkins pipeline setup',
          'Infrastructure as Code with Terraform, ARM templates',
          'Configuration management with Ansible, Puppet',
          'Automated testing integration and quality gates',
          'Deployment strategies - Blue/Green, Canary releases',
          'Monitoring and logging with Prometheus, Grafana, ELK stack'
        ],
      },
    ],
  }, 
  {
    heroImage:"/images/AWSSystemsManager.png",
    slug: 'aws-systems-manager',
    title: 'AWS Systems Manager',
    template: 'imageTop',
    heading: 'Streamlining Operations with AWS Systems Manager',
    description:
      'At HTPL, we leverage AWS Systems Manager to deliver comprehensive management and automation services for your AWS environments. As an AWS partner, we provide cutting-edge cloud solutions that enhance operational efficiency, security, and cost optimization. Our expertise ensures you can manage your AWS infrastructure effortlessly with automated operations and centralized control.',
    heading1: 'Why Choose HTPL for AWS Systems Manager?',
    description1: 'Our team of AWS-certified professionals has extensive experience in managing and optimizing AWS environments using AWS Systems Manager. We provide customized solutions tailored to meet your specific needs, ensuring optimal performance, security, and cost-efficiency with comprehensive end-to-end support.',
    image: '/images/aws-systems-manager.gif',
    benefits: [
      'Centralized management of all AWS resources from a single dashboard',
      'Automated patch management and compliance monitoring',
      'Secure remote access without bastion hosts or open ports',
      'Reduced operational overhead with automated tasks',
      'Enhanced security with encrypted parameter storage',
      'Comprehensive audit trails for compliance requirements'
    ],
    process: [
      'AWS Environment Assessment & Planning',
      'Systems Manager Setup & Configuration',
      'Automation Scripts & Runbooks Development',
      'Security & Compliance Implementation',
      'Monitoring & Dashboard Integration',
      'Ongoing Optimization & Support'
    ],
    featureSections: [
      {
        title: 'Operational Insights & Monitoring',
        items: [
          'Dashboard Integration - Consolidate operational data into a single comprehensive view',
          'Enhanced Visibility - Monitor infrastructure and application health in real-time',
          'CloudWatch Integration - Advanced monitoring and alerting capabilities',
          'OpsCenter - Centralized operations hub for issue management',
          'Explorer - Resource data aggregation and operational insights',
          'Incident Manager - Automated incident response and management'
        ],
      },
      {
        title: 'Automation & Configuration Management',
        items: [
          'Automated Tasks - Schedule and execute tasks using predefined or custom scripts',
          'State Manager - Ensure consistent configuration across all environments',
          'Automation Documents - Create and manage complex workflows',
          'Maintenance Windows - Schedule automated maintenance activities',
          'Change Manager - Track and approve infrastructure changes',
          'Application Manager - Manage applications and their dependencies'
        ],
      },
      {
        title: 'Security & Access Management',
        items: [
          'Session Manager - Secure shell and RDP access without open ports',
          'Parameter Store - Centralized, encrypted storage for configuration data',
          'Run Command - Execute commands remotely with fine-grained access controls',
          'Patch Manager - Automated security patching and compliance',
          'Inventory - Track software, configurations, and network settings',
          'Distributor - Securely distribute and install software packages'
        ],
      },
      {
        title: 'Advanced Management Features',
        items: [
          'AppConfig - Feature flag and configuration management',
          'Fleet Manager - Remote desktop access through web browser',
          'Compliance - Automated compliance scanning and reporting',
          'Quick Setup - Rapid deployment of common configurations',
          'Hybrid Activations - Manage on-premises servers alongside AWS',
          'Integration with AWS Services - Seamless connectivity with EC2, Lambda, and more'
        ],
      },
    ],
  }, 
  {
    heroImage:"/images/AWSServices.png",
    slug: 'aws-services',
    title: 'AWS Services',
    template: 'imageTop',
    heading: 'AWS Select Tier Services Partner',
    description:
      'HTPL has been a proud AWS Select Tier Services Partner for the past two years, delivering numerous successful projects and customized cloud solutions. Through our strong collaboration with AWS, we stay at the forefront of cloud innovation, enabling us to offer businesses unparalleled expertise, access to advanced tools, and tailored support. Our deep involvement in AWS programs ensures that we deliver the right solutions to help businesses accelerate their growth and maximize the potential of AWS services.',
    heading1: 'Industries We Serve',
    description1: 'HTPL revolutionizes the digital landscape by delivering cutting-edge AWS solutions across a vast array of industries. From healthcare to finance, retail to manufacturing, our tailored IT services empower businesses to thrive in today\'s dynamic environment.',
    image: '/images/aws-services.gif',
    benefits: [
      'AWS Select Tier Services Partner with proven track record',
      'Comprehensive solutions across 15+ technology domains',
      'Industry-specific expertise across 8 major sectors',
      'End-to-end cloud transformation and modernization',
      'Proven case studies with measurable business outcomes',
      '24/7 support and ongoing optimization services'
    ],
    process: [
      'Business Requirements & AWS Readiness Assessment',
      'Solution Architecture & Technology Selection',
      'Implementation & Integration Planning',
      'Deployment & Testing Across Environments',
      'Go-Live Support & Performance Optimization',
      'Ongoing Management & Continuous Improvement'
    ],
    featureSections: [
      {
        title: 'Solutions by Technology',
        items: [
          'Analytics - Data lakes, real-time analytics, and business intelligence solutions',
          'App Development & DevOps - CI/CD pipelines, containerization, and microservices',
          'Blockchain - Distributed ledger solutions and smart contracts',
          'Cloud Foundations - Landing zones, account setup, and governance',
          'Cloud Operations - Monitoring, automation, and operational excellence',
          'Compute - EC2, Lambda, containers, and serverless architectures',
          'Databases - RDS, DynamoDB, data warehousing, and migration',
          'Enterprise Resource Planning - SAP, Oracle, and custom ERP solutions',
          'Internet of Things (IoT) - Device management and data processing',
          'Migration & Modernization - Application and infrastructure transformation',
          'Networking - VPC, Direct Connect, and hybrid connectivity',
          'Resilience - Disaster recovery, backup, and business continuity',
          'Security, Identity & Compliance - IAM, encryption, and compliance frameworks',
          'Spatial Computing - AR/VR solutions and 3D applications',
          'Storage - S3, EFS, backup, and archival solutions'
        ],
      },
      {
        title: 'Industry Expertise',
        items: [
          'BFSI - Enhanced security, compliance, and customer experience solutions',
          'Manufacturing - Streamlined operations and increased efficiency through AWS',
          'Logistics - Supply chain visibility, cost reduction, and customer experience',
          'Education - Personalized and immersive learning experiences for students',
          'IT Services - Cloud-native applications with increased agility and reduced costs',
          'Healthcare - Patient engagement, medical billing, and clinical decision support',
          'Retail - E-commerce, omnichannel retailing, and inventory management',
          'Financial Services - Risk management, compliance, and fraud detection'
        ],
      },
      {
        title: 'Case Studies & Success Stories',
        items: [
          'Firstep Techno - Automated Windows password reset solution using AWS Systems Manager',
          'NEXGEN TECHTRONICS - Managed EC2 instance automation with enhanced security',
          'Enterprise Password Management - Secure and automated password handling with Parameter Store',
          'Cost Optimization Projects - Significant savings through automated resource management',
          'Security Enhancement - Continuous monitoring with CloudWatch and CloudTrail integration',
          'Operational Efficiency - Reduced manual effort and administrative overhead'
        ],
      },
      {
        title: 'AWS Partnership Benefits',
        items: [
          'Select Tier Services Partner status with AWS',
          'Access to AWS technical resources and advanced support',
          'Early access to new AWS services and features',
          'Co-marketing opportunities and joint go-to-market strategies',
          'AWS-certified professionals with deep technical expertise',
          'Proven methodology for successful AWS implementations'
        ],
      },
    ],
  }, 
  {
    heroImage: "/images/copilot-studio-hero.jpg",
    slug: 'microsoft-copilot-studio',
    title: 'Microsoft Copilot Studio',
    heading: 'AI-Powered Automation & Virtual Agents',
    description: 'Transform your business with Copilot Studio, a powerful low-code platform for building custom AI-powered virtual agents and automation solutions that enhance productivity and customer engagement.',
    description1: 'Leverage the power of AI to create intelligent virtual agents that understand natural language, automate routine tasks, and provide 24/7 support across multiple channels.',
    image: '/images/copilot-studio-feature.gif',
    template: 'imageTop',
    benefits: [
      'Reduce operational costs with AI-powered automation',
      'Improve customer satisfaction with 24/7 virtual assistance',
      'Seamless integration with Microsoft 365 and Dynamics 365',
      'No-code/low-code development environment',
      'Enterprise-grade security and compliance',
      'Scalable solutions for businesses of all sizes'
    ],
    process: [
      'Requirement Analysis & Use Case Definition',
      'Knowledge Base & Content Preparation',
      'Virtual Agent Design & Development',
      'Integration with Business Systems',
      'Testing & Quality Assurance',
      'Deployment & Continuous Improvement'
    ],
    featureSections: [
      {
        title: 'Core Features',
        items: [
          'AI-powered virtual agents with natural language understanding',
          'Drag-and-drop conversation designer',
          'Pre-built templates for common business scenarios',
          'Multi-channel deployment (web, Teams, mobile)',
          'Sentiment analysis and intent recognition',
          'Knowledge base integration'
        ]
      },
      {
        title: 'Advanced Capabilities',
        items: [
          'Custom AI models with Azure Machine Learning',
          'Omnichannel deployment options',
          'Advanced analytics and conversation insights',
          'Enterprise-grade security and compliance',
          'Seamless handoff to human agents',
          'Continuous learning and improvement'
        ]
      },
      {
        title: 'Integration Options',
        items: [
          'Microsoft 365 and Dynamics 365 integration',
          'REST API for custom integrations',
          'Power Platform connectivity',
          'Azure Active Directory authentication',
          'Custom connectors for business systems',
          'Webhook support for real-time data'
        ]
      }
    ]
  },
  {
    heroImage: '/images/image (6).png',
    slug: 'hsm-key-management-as-a-service',
    title: 'HSM and Key Management as a service',
    heading: 'Secure cryptographic keys with managed HSM and KMS services',
    description: 'Deliver enterprise-grade key protection with Hardware Security Module (HSM) and managed Key Management as a Service (KMS). Our solution helps you safeguard cryptographic keys, enforce access controls, and maintain compliance across cloud and hybrid environments.',
    description1: 'We provide managed HSM and key management services that deliver secure key lifecycle management, encryption policy enforcement, and integration with cloud platforms for secure data protection.',
    heroCards: [
      { title: 'Enterprise Grade Security', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777263898/Mask_group-11_qcsizi.png' },
      { title: 'Cloud Delivered & Scalable', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777263906/Mask_group-8_xof0qz.png' },
      { title: 'Regulatory Compliant', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777263914/Mask_group-9_lbz0gm.png' },
      { title: 'High Performance & Available', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777263920/Mask_group-10_pway4m.png' }
    ],
    offeringCards: [
      {
        title: 'HSM-as-a-Service (HSMaaS)',
        icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264182/Mask_group-7_zaqhyq.png',
        items: [
          'FIPS 140-2/3 compliant Hardware Security Modules hosted in secure cloud',
          'Root of Trust for payment, identity, and critical infrastructure',
          'Use cases: UPI, cards, wallets, tokenisation, PKI, digital signatures, encryption',
          'High availability with clustering and geo-redundancy'
        ]
      },
      {
        title: 'Key Management-as-a-Service (KMSaaS)',
        icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264188/Mask_group-6_jcdvtf.png',
        items: [
          'Centralized lifecycle management of cryptographic keys',
          'Support for data-at-rest, data-in-transit & data-in-use encryption',
          'API-driven crypto operations with KMIP & REST interfaces',
          'Scalable to manage millions of keys across hybrid and multi-cloud environments'
        ]
      }
    ],
    complianceCards: [
      { title: 'Payment security controls, tokenisation mandates, data localisation', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264551/Group_2_vvvn0w.png' },
      { title: 'UPI security framework, encryption and key management standards', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264568/image_6_dh9kxx.png' },
      { title: 'Aadhaar data security, encryption and HSM usage for authentication ecosystem', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264576/image_7_l5y045.png' },
      { title: 'Cybersecurity guidelines, cloud and data protection frameworks', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264584/image_8_ieteom.png' },
      { title: 'Smart meter security guidelines, device identity and encryption controls', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264591/image_9_fkl2mb.png' },
      { title: 'Smart city and urban digital infrastructure security', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264604/image_11_zziubh.png' },
      { title: 'Secure financial transactions and project protection', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264598/image_10_mey3uh.png' },
      { title: 'Data encryption, key control and access governance, breach risk mitigation', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264562/Group_3_zb8agy.png' }
    ],
    industryCards: [
      { title: 'BFSI & Fintech', description: 'UPI, card tokenisation, mobile banking, digital wallets', image: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264971/Rectangle_22_ny0cay.jpg' },
      { title: 'Government & e-Governance', description: 'e-Governance, Aadhaar, digital identity, citizen services', image: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264978/Rectangle_23_axqtk2.jpg' },
      { title: 'Energy & Utilities', description: 'Smart metering, grid security, energy data protection', image: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264989/Rectangle_24_ydteqw.jpg' },
      { title: 'Enterprises', description: 'Data encryption, API security, PKI, secrets management', image: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777264997/Rectangle_25_fzdiie.jpg' }
    ],
    whyChooseItems: [
      { title: 'India-focused compliance-first architecture', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265239/Mask_group_xwrpd5.png' },
      { title: 'Cloud-native with on-prem integration flexibility', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265245/Mask_group-1_tzscyv.png' },
      { title: 'Designed for billion-scale transactions', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265253/Mask_group-2_xilvig.png' },
      { title: 'Backed by certified HSM infrastructure (FIPS 140-2/3)', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265261/Mask_group-3_lj2fpk.png' },
      { title: 'End-to-end security across data, keys and applications', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265239/Mask_group_xwrpd5.png' },
      { title: '24x7 expert support & proactive monitoring', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265269/Mask_group-5_rcned8.png' }
    ],
    certifications: [
      { label: 'FIPS 140-2/3 CERTIFIED', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265595/Frame_7_qqqggs.png' },
      { label: 'ISO 27001 CERTIFIED', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265599/Frame_8_vcu8un.png' },
      { label: 'SOC 2 TYPE II COMPLIANT', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265605/Frame_9_cjpkpd.png' },
      { label: 'Secure Today. Compliant Always. Trusted for Tomorrow.', icon: 'https://res.cloudinary.com/daibxvgcy/image/upload/v1777265611/Frame_10_uirmys.png' }
    ],
    image: '/images/image%20(6).png',
    template: 'imageTop',
    benefits: [
      'Centralized key lifecycle management and rotation',
      'Hardware-backed cryptographic key protection',
      'Simplified compliance for PCI DSS, GDPR, and ISO standards',
      'Secure integration with cloud services and applications',
      'Granular access controls and audit logging',
      'Improved data encryption and key usage transparency'
    ],
    process: [
      'Key strategy and requirements assessment',
      'Secure HSM deployment and configuration',
      'Key creation, import, and access policy setup',
      'Integration with cloud workloads and applications',
      'Monitoring, auditing, and regular key rotation',
      'Ongoing support and compliance review'
    ],
    featureSections: [
      {
        title: 'Core Capabilities',
        items: [
          'Dedicated HSM-based key protection',
          'Managed Key Management Service (KMS)',
          'Policy-driven key usage controls',
          'Automated key rotation and lifecycle support',
          'Audit-ready access logging',
          'Secure key backup and recovery'
        ]
      },
      {
        title: 'Security Benefits',
        items: [
          'Protection against key theft and unauthorized use',
          'Strong compliance posture for regulated workloads',
          'Encryption at rest and in transit with managed keys',
          'Reduced operational risk through centralized management',
          'Integration with identity and access management systems',
          'End-to-end data security for hybrid environments'
        ]
      },
      {
        title: 'Integration Options',
        items: [
          'Cloud-native integration with Azure, AWS, and GCP',
          'Support for application-level encryption APIs',
          'Key access management via IAM and RBAC',
          'Secure SDK and client library support',
          'Multi-tenant and hybrid architecture compatibility',
          'Real-time key usage monitoring and alerts'
        ]
      }
    ]
  }
];




export const products: Item[] = [ 
  {
    heroImage:"/images/ProposalHub.png",
    slug: 'proposal-hub',
    title: 'Proposal Hub',
    template: 'default',
    heading: 'Proposal Hub: Effortless Proposal Creation at Your Fingertips',
    description:
      "Streamline your client interactions with HTPL's intuitive Proposal Hub, a powerful Power App designed to generate professional proposals with just a few mandatory details. Built on the robust M365 ecosystem (Power Apps, Power Automate, SharePoint, and Exchange), Proposal Hub revolutionizes how businesses create, manage, and track proposals.",
    heading1: 'Why Choose Proposal Hub?',
    description1: 'Proposal Hub is a fully automated end-to-end proposal creation system that embraces intelligent automation, easy integration, and quick implementation. Save time, enhance accuracy, and focus on what truly matters—building strong client relationships with our user-friendly application.',
    image: '/images/proposalhub.gif',
    benefits: [
      'Automated proposal generation with minimal input required',
      'Complete proposal history and tracking system',
      'Seamless M365 ecosystem integration',
      'Professional templates and customizable layouts',
      'Real-time collaboration and approval workflows',
      'Enhanced accuracy with automated data validation'
    ],
    process: [
      'Client Information Input',
      'Service Selection & Configuration',
      'Automated Content Generation',
      'Review & Customization',
      'Approval Workflow Processing',
      'Final Proposal Delivery'
    ],
    featureSections: [
      {
        title: 'Core Features',
        items: [
          'Intelligent proposal templates with dynamic content generation',
          'Integration with SharePoint for document management',
          'Power Automate workflows for approval processes',
          'Exchange integration for automated email notifications',
          'Real-time collaboration tools for team input',
          'Mobile-responsive design for on-the-go access'
        ],
      },
      {
        title: 'Business Benefits',
        items: [
          'Reduced proposal creation time by up to 80%',
          'Improved proposal consistency and professional appearance',
          'Enhanced client experience with faster turnaround times',
          'Complete audit trail for compliance and tracking',
          'Scalable solution that grows with your business',
          'Cost-effective alternative to expensive proposal software'
        ],
      },
    ],
  },
  {
    heroImage:"/images/DocFlow.png",
    slug: 'doc-flow',
    title: 'DocFlow',
    template: 'imageRight',
    heading: 'DocFlow: Advanced Document Approval System',
    description:
      "DocFlow revolutionizes document management by enabling seamless multi-hierarchical approval processes. Built on the powerful M365 ecosystem, this sophisticated application ensures accuracy, efficient communication, and complete transparency throughout your organization's document approval workflows.",
    heading1: 'Streamlined Approval Workflows',
    description1: 'With DocFlow, organizations can establish dynamic approval chains where approvers are automatically selected based on department, role, or custom criteria. The system maintains complete transparency while expediting the approval process through intelligent automation.',
    image: '/images/doc-flow.gif',
    benefits: [
      'Multi-level approval workflows with automatic routing',
      'Dynamic approver selection based on department rules',
      'Complete audit trail and compliance tracking',
      'Reduced approval time with automated notifications',
      'Enhanced security with role-based access controls',
      'Seamless integration with existing M365 infrastructure'
    ],
    process: [
      'Document Submission & Classification',
      'Automatic Approver Assignment',
      'Multi-Level Review Process',
      'Stakeholder Notifications & Reminders',
      'Final Approval & Document Publishing',
      'Archive & Compliance Reporting'
    ],
    featureSections: [
      {
        title: 'Approval Management',
        items: [
          'Configurable multi-hierarchical approval chains',
          'Dynamic approver assignment based on business rules',
          'Parallel and sequential approval workflows',
          'Escalation mechanisms for overdue approvals',
          'Delegation capabilities for temporary assignments',
          'Bulk approval processing for efficiency'
        ],
      },
      {
        title: 'Communication & Tracking',
        items: [
          'Automated email notifications and reminders',
          'Real-time status tracking and dashboards',
          'Comments and feedback collection system',
          'Version control and document history',
          'Mobile notifications for urgent approvals',
          'Integration with Teams for collaborative discussions'
        ],
      },
    ],
  },
  {
    heroImage:"/images/Helpdesk365.png",
    slug: 'helpdesk-365',
    title: 'Helpdesk 365',
    template: 'imageTop',
    heading: 'Helpdesk 365: Comprehensive IT Support Solution',
    description:
      "Helpdesk 365 is HTPL's advanced ticketing and support management system designed specifically for small to medium businesses. Built on the M365 ecosystem, it provides comprehensive IT support capabilities with integrated analytics, automated workflows, and seamless user experience.",
    heading1: 'Complete Support Management Platform',
    description1: 'Transform your IT support operations with Helpdesk 365\'s intelligent ticketing system, real-time analytics, and automated workflows. Designed for businesses that need professional support management without the complexity of enterprise solutions.',
    image: '/images/helpdesk365.gif',
    benefits: [
      'Centralized ticket management with automated routing',
      'Real-time Power BI analytics and reporting dashboards',
      'Improved response times with SLA management',
      'Enhanced customer satisfaction tracking',
      'Reduced operational costs through automation',
      'Scalable solution that grows with your business'
    ],
    process: [
      'Ticket Creation & Classification',
      'Automatic Assignment & Prioritization',
      'Collaborative Problem Resolution',
      'Progress Tracking & Communication',
      'Quality Assurance & Feedback',
      'Resolution & Knowledge Base Update'
    ],
    featureSections: [
      {
        title: 'Ticket Management Features',
        items: [
          'Intelligent ticket routing based on category and priority',
          'Collaborative evaluation and consultation workflows',
          'Automated escalation for critical issues',
          'Complete ticket history and audit trails',
          'SLA tracking and performance monitoring',
          'Integration with external systems and tools'
        ],
      },
      {
        title: 'Analytics & Reporting',
        items: [
          'Real-time Power BI dashboards for management insights',
          'Comprehensive ticket status and trend analysis',
          'Performance metrics and KPI tracking',
          'Customer satisfaction surveys and feedback analysis',
          'Resource utilization and workload distribution reports',
          'Predictive analytics for proactive support planning'
        ],
      },
      {
        title: 'Advanced Capabilities',
        items: [
          'Accurate time tracking and billing integration',
          'Customizable workflows and business rules',
          'Knowledge base integration and article suggestions',
          'Multi-channel support (email, web, phone, chat)',
          'Mobile app for technicians and end users',
          'Asset management and configuration tracking'
        ],
      },
    ],
  },


];
export function getServiceBySlug(slug: string): Item | undefined {
  return services.find((s) => s.slug === slug);
}

export function getProductBySlug(slug: string): Item | undefined {
  return products.find((s) => s.slug === slug);
}


export const baseUrl = process.env.NEXT_PUBLIC_ALLOWED_ORIGIN || 'https://www.himtechdist.com';
